import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Tooltip, Typography } from '@mui/material';
import { Icon } from '@iconify/react';
import { useDispatch } from 'react-redux';
// NOT `fDateTime` from utils/formatTime — it parses with `new Date()`, which
// cannot read this backend's DRF wire format ("20-08-2026 17:16:31.123456Z")
// and returns '' for every value, silently. That is exactly how this banner
// shipped rendering "They messaged again on ." with no date (ISS-025B).
// `formatApiDateTime` also encodes the non-obvious bit: the trailing Z is a
// LITERAL, not a UTC marker, so naive parsing lands an hour out in BST.
import { formatApiDateTime } from 'src/utils/taskDateUtils';
import { restoreArchivedLead } from 'src/redux/slices/lead/coreThunks';
import { ignoreReEngagement } from 'src/redux/slices/whatsappV2';
import { updateError } from 'src/redux/slices/user';
import IgnoreReEngagementDialog from './IgnoreReEngagementDialog';
import { COLORS } from './styles';

/**
 * ISS-025 — archived-lead re-engagement banner.
 *
 * Shown when a patient we ARCHIVED has messaged on WhatsApp again and nobody
 * has dealt with it yet (backend sets `thread.re_engaged_at`).
 *
 * Before this existed the same event produced two different outcomes: the lead
 * was either silently un-archived by the webhook, or the conversation was
 * linked to a still-archived lead and filtered out of every inbox tab and
 * counter while notifications still fired. Now the conversation always
 * surfaces, the lead always stays archived, and a human decides.
 *
 * The banner does NOT unblock replying by itself — the backend GDPR gates stay
 * authoritative. It explains WHY the composer is blocked and offers the two
 * ways out: restore the patient, or ignore the re-enquiry.
 *
 * ISS-025B — the second action shipped as a ghost button labelled "Dismiss".
 * Mechanically it was already correct (close the thread; the backend clears
 * the marker and re-archives it), but reps read the label as "hide this
 * notice", could not predict the consequence, and so left these conversations
 * sitting in the inbox. It is now "Ignore", styled as a real secondary action,
 * behind a shared confirm that states what happens. Same thunk underneath.
 */

const wrapperSx = {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    px: 1.5,
    py: 1,
    backgroundColor: COLORS.warningBg,
    borderBottom: `1px solid ${COLORS.warningBorder}`,
};

// Default parameters rather than `defaultProps`: React 18.3 deprecates
// defaultProps on function components and warns on every render.
const ReEngagementBanner = ({
    reEngagedAt = null,
    archivedAt = null,
    contactName = '',
    threadId = null,
    leadId = null,
    practiceId = null,
    leadIsArchived = null,
    onIgnored = null,
}) => {
    const dispatch = useDispatch();
    const [restoreRequested, setRestoreRequested] = useState(false);
    const [busy, setBusy] = useState(false);
    const [confirmIgnoreOpen, setConfirmIgnoreOpen] = useState(false);
    const [resolved, setResolved] = useState(false);

    if (!reEngagedAt) {
        return null;
    }

    // RCA 2026-08-28 — `re_engaged_at` and `lead.is_archived` are two
    // independently-stored flags, and this banner used to read only the first.
    // When a thread's ownership moves to an ACTIVE lead the marker is left
    // behind, and the banner then asserted "This lead is archived" over a lead
    // that never was, offering a Restore that could only 404 (thread 11928:
    // four attempts over two days). The backend no longer strands the marker
    // and no longer 404s, but the honest gate belongs here too — `is_archived`
    // has been on the wire all along via BasicLeadSerializer.
    //
    // Strict `=== false`: an absent/loading lead payload is UNKNOWN, not
    // active, and must not suppress a legitimate banner.
    if (leadIsArchived === false || resolved) {
        return null;
    }

    const reEngagedLabel = formatApiDateTime(reEngagedAt);
    const archivedLabel = formatApiDateTime(archivedAt);

    const handleRestore = async () => {
        if (!practiceId || !leadId) {
            dispatch(
                updateError({
                    message: 'Still loading this conversation — please try again in a moment.',
                    success: false,
                })
            );
            return;
        }
        setBusy(true);
        try {
            const response = await restoreArchivedLead(practiceId, leadId);
            // 200 { already_active: true } — the lead was never archived, or
            // was restored already; the marker was stale. Nothing is scheduled,
            // so say that, and drop the banner rather than leave a false claim
            // and a dead button on screen.
            if (response?.data?.already_active) {
                setResolved(true);
                dispatch(
                    updateError({
                        message: `${contactName || 'This patient'} is already active — you can reply as normal.`,
                        success: true,
                    })
                );
                return;
            }
            // A 404 means this conversation is not linked to an archived lead
            // of this practice at all — the pre-fix backend's answer to exactly
            // the state above, and still the answer if the lead has moved
            // practice. Retrying cannot succeed, so stop offering it. NOTE the
            // marker may still be set server-side on that path, so the banner
            // can return on reopen; the copy therefore states the fact rather
            // than promising this is resolved.
            if (response?.status === 404) {
                setResolved(true);
                dispatch(
                    updateError({
                        message: 'This conversation is not linked to an archived patient — there is nothing to restore.',
                        success: true,
                    })
                );
                return;
            }
            // `{ __inFlight: true }` means an identical restore is already in
            // flight (the helper single-flights per leadId) — treat it as a
            // success for UI purposes rather than showing a spurious error.
            if (response?.__inFlight || response?.status === 200) {
                // The restore endpoint is ASYNC: it returns `{ scheduled: true }`
                // and a Celery worker performs the restore. So we must NOT claim
                // the lead is active yet, and must not navigate anywhere. The
                // banner clears on its own once the backend `lead_restored`
                // receiver wipes `re_engaged_at` and the thread is refetched.
                setRestoreRequested(true);
                dispatch(
                    updateError({
                        message: 'Restore requested — this patient will be active again shortly.',
                        success: true,
                    })
                );
            } else {
                dispatch(
                    updateError({
                        message: 'Could not restore this lead. Please try again.',
                        success: false,
                    })
                );
            }
        } catch (error) {
            dispatch(
                updateError({
                    message: 'Could not restore this lead. Please try again.',
                    success: false,
                    cause: error,
                })
            );
        } finally {
            setBusy(false);
        }
    };

    // ISS-025B — was `handleDismiss`, wired to a ghost button labelled
    // "Dismiss". The mechanics were already right; the label was not. Reps read
    // "Dismiss" as "close this notice" and left these rows in the inbox
    // indefinitely rather than risk an action they could not predict. Same
    // thunk underneath, honest name on top, and the consequence spelled out in
    // a shared confirm before anything happens.
    const handleIgnore = async () => {
        if (!threadId) return;
        setBusy(true);
        let result;
        try {
            result = await dispatch(ignoreReEngagement(threadId, leadId, contactName));
        } finally {
            setBusy(false);
            setConfirmIgnoreOpen(false);
        }
        // Only hand control back on success. The thunk surfaces its own error
        // toast on failure, and the conversation is still very much in the
        // inbox — closing the pane there would tell the rep the opposite of
        // what happened.
        if (result?.ok) {
            onIgnored?.();
        }
    };

    return (
        <Box sx={wrapperSx} role="status" aria-label="Archived lead has messaged again">
            <Icon
                icon="mdi:archive-alert-outline"
                width={18}
                height={18}
                style={{ color: COLORS.warningDark, flexShrink: 0 }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: COLORS.warningDark, lineHeight: 1.3 }}>
                    This lead is archived
                    {archivedLabel ? ` \u00b7 archived ${archivedLabel}` : ''}
                </Typography>
                <Typography sx={{ fontSize: 12, color: COLORS.textPrimary, lineHeight: 1.3 }}>
                    {(() => {
                        if (restoreRequested) {
                            return 'Restore requested — this patient will be active again shortly, then you can reply.';
                        }
                        // Degrade to the sentence without the date rather than
                        // to "on ." — an unparseable or absent timestamp must
                        // not leave a dangling clause.
                        return reEngagedLabel
                            ? `They messaged again on ${reEngagedLabel}. Restore the lead to reply.`
                            : 'They have messaged again. Restore the lead to reply.';
                    })()}
                </Typography>
            </Box>
            {!restoreRequested && (
                <>
                    {/* Outlined, not a ghost text button. It is a real action
                        with a real consequence and needs to look like one —
                        visibly secondary to Restore, but visibly a button. */}
                    <Tooltip title="Keeps the patient archived. This chat leaves your inbox until they message again.">
                        <span>
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<Icon icon="mdi:eye-off-outline" width={16} height={16} />}
                                onClick={() => setConfirmIgnoreOpen(true)}
                                disabled={busy}
                                sx={{
                                    color: COLORS.warningDark,
                                    borderColor: COLORS.warningBorder,
                                    whiteSpace: 'nowrap',
                                    '&:hover': {
                                        borderColor: COLORS.warningDark,
                                        backgroundColor: COLORS.warningBg,
                                    },
                                }}
                            >
                                Ignore
                            </Button>
                        </span>
                    </Tooltip>
                    <Button
                        size="small"
                        variant="contained"
                        onClick={handleRestore}
                        disabled={busy}
                        sx={{
                            backgroundColor: COLORS.primary,
                            '&:hover': { backgroundColor: COLORS.primaryDark },
                            whiteSpace: 'nowrap',
                        }}
                    >
                        Restore &amp; reply
                    </Button>
                    <IgnoreReEngagementDialog
                        open={confirmIgnoreOpen}
                        onClose={() => setConfirmIgnoreOpen(false)}
                        onConfirm={handleIgnore}
                        contactName={contactName}
                        archivedAt={archivedAt}
                        busy={busy}
                    />
                </>
            )}
        </Box>
    );
};

ReEngagementBanner.propTypes = {
    reEngagedAt: PropTypes.string,
    archivedAt: PropTypes.string,
    contactName: PropTypes.string,
    threadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    // true = archived, false = active, null/undefined = not yet known.
    leadIsArchived: PropTypes.bool,
    onIgnored: PropTypes.func,
};

export default ReEngagementBanner;
